import { FunctionComponent, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import ClickAwayListener from 'components/CreateBooking/UI/ClickAwayListener';
import MembershipIcon from 'components/Checkout2/Memberships/MembershipIcon';

import styles from './MembershipServiceSelect.module.scss';

export interface MembershipServiceSelectOption {
    id: number;
    serviceTitle: string;
    membershipName: string;
    kindLabel: string;
    badgeText: string;
    disabled?: boolean;
}

export interface MembershipServiceSelectProps {
    placeholder?: string;
    selected: MembershipServiceSelectOption | null;
    options: MembershipServiceSelectOption[];
    onSelect: (option: MembershipServiceSelectOption) => void;
    className?: string;
}

function BadgeContent({ text }: { text: string }) {
    return (
        <span className={styles.badge}>
            <MembershipIcon className={styles.membershipIcon} />
            {text}
        </span>
    );
}

const MembershipServiceSelect: FunctionComponent<MembershipServiceSelectProps> = ({
    placeholder = 'Select a Service',
    selected,
    options,
    onSelect,
    className
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const handleSelect = (opt: MembershipServiceSelectOption) => {
        if (opt.disabled) return;
        onSelect(opt);
        setIsCollapsed(true);
    };

    const headerBody = (
        <div className={styles.header}>
            <div className={styles.headerLeft}>
                {selected ? (
                    <>
                        <span className={styles.headerTitle}>{selected.serviceTitle}</span>
                        <span className={styles.headerMeta}>
                            {selected.membershipName} · {selected.kindLabel}
                        </span>
                    </>
                ) : (
                    <span className={clsx(styles.headerTitle, styles.headerTitlePlaceholder)}>{placeholder}</span>
                )}
            </div>
            {selected && (
                <div className={styles.headerBadgeWrap}>
                    <BadgeContent text={selected.badgeText} />
                </div>
            )}
            <FontAwesomeIcon
                icon={isCollapsed ? faChevronDown : faChevronUp}
                className={styles.chevron}
            />
        </div>
    );

    const list = (
        <div
            className={styles.list}
            role="listbox"
        >
            {options.map((opt, index) => (
                <button
                    key={`${opt.id}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={selected != null && selected.id === opt.id}
                    disabled={opt.disabled}
                    className={clsx(styles.row, opt.disabled && styles.rowDisabled)}
                    onClick={() => handleSelect(opt)}
                >
                    <div className={styles.rowLeft}>
                        <div className={styles.rowTitle}>{opt.serviceTitle}</div>
                        <div className={styles.rowSubtitle}>{opt.membershipName}</div>
                        <div className={styles.rowKind}>{opt.kindLabel}</div>
                    </div>
                    <BadgeContent text={opt.badgeText} />
                </button>
            ))}
        </div>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <div className={clsx(styles.container, className)}>
                <div className={styles.dropdownContainer}>
                    {isCollapsed ? (
                        <div
                            className={clsx(styles.border, styles.collapsedContainer)}
                            onClick={() => setIsCollapsed(false)}
                        >
                            {headerBody}
                        </div>
                    ) : (
                        <div className={clsx(styles.border, styles.expandedBorder, styles.expandedContainer)}>
                            <div onClick={() => setIsCollapsed(true)}>{headerBody}</div>
                            {list}
                        </div>
                    )}
                </div>
            </div>
        </ClickAwayListener>
    );
};

export default MembershipServiceSelect;
